import { format as formatDate, isValid, parse, parseISO } from 'date-fns';

/** Field types that contain date values */
export const DATE_FIELD_TYPES = ['DT', 'DA', 'YM', 'YO'] as const;

export const isDateField = (fieldType?: string): boolean =>
  DATE_FIELD_TYPES.includes(fieldType as (typeof DATE_FIELD_TYPES)[number]);

const MONTH_ABBREVS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Slice lengths for each field type */
const FIELD_TYPE_SLICE: Record<string, number> = {
  DT: 10, // DT is currently disabled in admin UI; slice same as DA
  DA: 10,
  YM: 7,
  YO: 4,
};

const YEAR_ONLY_PATTERN = /^(\d{4})$/;
const YEAR_MONTH_PATTERN = /^(\d{4})-(\d{2})$/;
const YEAR_MONTH_SHORT_PATTERN = /^(\d{1,2})\/(\d{4})$/;
const FULL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;
const FALLBACK_PARSE_FORMATS_BY_FIELD_TYPE: Record<string, string[]> = {
  YO: ['yyyy'],
  YM: ['MMMM yyyy', 'MMM yyyy', 'M/yyyy', 'MM/yyyy'],
  DA: ['MMMM d, yyyy', 'd MMMM yyyy', 'MMM d, yyyy', 'MM-dd-yyyy'],
  DT: ['MMMM d, yyyy', 'd MMMM yyyy', 'MMM d, yyyy', 'MM-dd-yyyy'],
};

const buildLocalDate = (year: number, month: number = 1, day: number = 1): Date | null => {
  const candidate = new Date(year, month - 1, day);
  if (
    Number.isNaN(candidate.getTime()) ||
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }
  return candidate;
};

const stripTimeFromDate = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const tryParseWithKnownFormats = (value: string, fieldType?: string): Date | null => {
  const formats = FALLBACK_PARSE_FORMATS_BY_FIELD_TYPE[fieldType ?? ''] ?? [];

  for (const pattern of formats) {
    const parsed = parse(value, pattern, new Date());
    if (isValid(parsed) && formatDate(parsed, pattern) === value) {
      return stripTimeFromDate(parsed);
    }
  }

  return null;
};

/** Parse date parts from an ISO-sliced string. Returns null if unparseable. */
const parseDateParts = (
  sliced: string,
): { year: string; month: number; day: number; hours: number; minutes: number } | null => {
  // Match YYYY-MM-DD with optional THH:MM or space HH:MM
  const match = sliced.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?(?:[T ](\d{2}):(\d{2}))?/);
  if (!match) return null;
  const month = parseInt(match[2], 10);
  if (month < 1 || month > 12) return null;
  return {
    year: match[1],
    month,
    day: match[3] ? parseInt(match[3], 10) : 0,
    hours: match[4] ? parseInt(match[4], 10) : 0,
    minutes: match[5] ? parseInt(match[5], 10) : 0,
  };
};

/** Format a date-only value (DA) with the given style */
const formatDA = (parts: { year: string; month: number; day: number }, style: string): string => {
  const { year, month, day } = parts;
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  switch (style) {
    case 'us':
      return `${month}/${day}/${year}`;
    case 'us-padded':
      return `${mm}-${dd}-${year}`;
    case 'eu':
      return `${day}/${month}/${year}`;
    case 'long':
      return `${MONTH_FULL[month - 1]} ${day}, ${year}`;
    case 'long-eu':
      return `${day} ${MONTH_FULL[month - 1]} ${year}`;
    case 'abbr':
      return `${MONTH_ABBREVS[month - 1]} ${day}, ${year}`;
    case 'iso':
    default:
      return `${year}-${mm}-${dd}`;
  }
};

/** Format a year-month value (YM) with the given style */
const formatYM = (parts: { year: string; month: number }, style: string): string => {
  const { year, month } = parts;
  switch (style) {
    case 'short':
      return `${month}/${year}`;
    case 'long':
      return `${MONTH_FULL[month - 1]} ${year}`;
    case 'abbr':
      return `${MONTH_ABBREVS[month - 1]} ${year}`;
    case 'iso':
    default:
      return `${year}-${String(month).padStart(2, '0')}`;
  }
};

/**
 * Format a raw ISO date string for read-only display based on field type and style.
 * Uses string slicing (not Date parsing) to avoid timezone-related day shifts.
 *
 * @param rawValue - ISO date string (e.g., "2024-03-15T10:30:00")
 * @param fieldType - Field precision: 'DT', 'DA', 'YM', 'YO'
 * @param style - Display style: 'iso', 'us', 'us-padded', 'eu', 'long', 'long-eu', 'short', 'abbr'
 */
export const formatDateDisplay = (rawValue: string, fieldType?: string, style?: string): string => {
  if (!rawValue) return '';

  const effectiveStyle = style || 'iso';
  const len = FIELD_TYPE_SLICE[fieldType ?? ''] ?? 10;
  const sliced = rawValue.length >= len && /^\d{4}-/.test(rawValue) ? rawValue.slice(0, len) : rawValue;

  const parts = parseDateParts(sliced);
  if (!parts) return sliced; // Unparseable — return as-is

  switch (fieldType) {
    case 'YM':
      return formatYM(parts, effectiveStyle);
    case 'YO':
      return parts.year;
    case 'DT': // DT is currently disabled in admin; treat same as DA
    case 'DA':
    default:
      return formatDA(parts, effectiveStyle);
  }
};

/**
 * Parse a stored or display date value into a local Date for the picker.
 */
export const parseDateInputValue = (value: string | null | undefined, fieldType?: string): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const yearOnlyMatch = trimmed.match(YEAR_ONLY_PATTERN);
  if (yearOnlyMatch) {
    return buildLocalDate(Number.parseInt(yearOnlyMatch[1], 10));
  }

  const yearMonthMatch = trimmed.match(YEAR_MONTH_PATTERN);
  if (yearMonthMatch) {
    return buildLocalDate(Number.parseInt(yearMonthMatch[1], 10), Number.parseInt(yearMonthMatch[2], 10));
  }

  if (fieldType === 'YM') {
    const shortYearMonthMatch = trimmed.match(YEAR_MONTH_SHORT_PATTERN);
    if (shortYearMonthMatch) {
      return buildLocalDate(Number.parseInt(shortYearMonthMatch[2], 10), Number.parseInt(shortYearMonthMatch[1], 10));
    }
  }

  const fullDateMatch = trimmed.match(FULL_DATE_PATTERN);
  if (fullDateMatch) {
    return buildLocalDate(
      Number.parseInt(fullDateMatch[1], 10),
      Number.parseInt(fullDateMatch[2], 10),
      Number.parseInt(fullDateMatch[3], 10),
    );
  }

  const isoParsed = parseISO(trimmed);
  if (isValid(isoParsed)) {
    return stripTimeFromDate(isoParsed);
  }

  return tryParseWithKnownFormats(trimmed, fieldType);
};

/**
 * Serialize a picker Date into the compact string expected by the field precision.
 */
export const serializeDateValue = (date: Date | null | undefined, fieldType?: string): string => {
  if (!date || !isValid(date)) return '';

  switch (fieldType) {
    case 'YO':
      return formatDate(date, 'yyyy');
    case 'YM':
      return formatDate(date, 'yyyy-MM');
    case 'DT':
    case 'DA':
    default:
      return formatDate(date, 'yyyy-MM-dd');
  }
};

/**
 * Normalize a stored or display date value to the field's compact precision format.
 */
export const normalizeDateValue = (value: string | null | undefined, fieldType?: string): string => {
  if (!value) return '';
  const parsed = parseDateInputValue(value, fieldType);
  return parsed ? serializeDateValue(parsed, fieldType) : value;
};

/**
 * Map a DA/DT display style to the closest YM equivalent.
 * Needed when facet values are at month precision but the field is configured as DA/DT.
 */
const mapStyleToYM = (style: string): string => {
  switch (style) {
    case 'iso':
      return 'iso';
    case 'us':
    case 'us-padded':
    case 'eu':
      return 'short';
    case 'long':
    case 'long-eu':
      return 'long';
    case 'abbr':
      return 'abbr';
    default:
      return 'iso';
  }
};

/**
 * Format a date filter value for chip/facet display.
 * When fieldType and style are provided, uses the admin-configured display style.
 * Detects the actual precision from the value pattern to handle mismatches
 * (e.g., DA field with monthly facet buckets).
 * Otherwise falls back to abbreviated format.
 */
export const formatDateFilterValue = (value: string, fieldType?: string, style?: string): string => {
  if (fieldType && style) {
    // Detect actual precision from value pattern — facets may differ from field precision
    if (/^\d{4}$/.test(value)) {
      return formatDateDisplay(value, 'YO', 'iso');
    }
    if (/^\d{4}-\d{2}$/.test(value)) {
      // YM value — map DA/DT styles to closest YM equivalents
      const ymStyle = ['DA', 'DT'].includes(fieldType) ? mapStyleToYM(style) : style;
      return formatDateDisplay(value, 'YM', ymStyle);
    }
    // Full date — use field's configured precision and style
    return formatDateDisplay(value, fieldType, style);
  }

  if (/^\d{4}$/.test(value)) return value;

  const ymMatch = value.match(/^(\d{4})-(\d{2})$/);
  if (ymMatch) {
    const monthIdx = parseInt(ymMatch[2], 10) - 1;
    return `${MONTH_ABBREVS[monthIdx]} ${ymMatch[1]}`;
  }

  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateMatch) {
    const monthIdx = parseInt(dateMatch[2], 10) - 1;
    const day = parseInt(dateMatch[3], 10);
    return `${MONTH_ABBREVS[monthIdx]} ${day}, ${dateMatch[1]}`;
  }

  return value;
};
